// src/components/CitizenRouteSync.jsx
// Tenant-safe, non-destructive synchronization between the current route,
// CitizenContext, and legacy/scoped browser-storage keys.
//
// Behaviour:
// - Supports both tenant-prefixed and unprefixed Citizen routes.
// - Prioritizes Citizen ID from the route, then navigation state.
// - Uses cached/state Citizen data immediately and refreshes it through the
//   shared authenticated HTTP service.
// - Synchronizes explicit Visit IDs from query/state to both scoped and legacy
//   keys.
// - Restores a Citizen-scoped Visit while entering an encounter.
// - Clears global Visit context only when switching Citizens or intentionally
//   entering Add Visit; ordinary navigation does not discard the selected Visit.
// - Never clears the selected Citizen merely because a route has no Citizen ID.

import React, { useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useCitizen } from '../CitizenContext';
import http from '../services/http';

function stringValue(value) {
  if (value === undefined || value === null) return '';
  const normalized = String(value).trim();
  return normalized === 'undefined' || normalized === 'null' ? '' : normalized;
}

function decodeValue(value) {
  const normalized = stringValue(value);
  if (!normalized) return '';

  try {
    return decodeURIComponent(normalized);
  } catch {
    return normalized;
  }
}

function readJson(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed === undefined || parsed === null ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage failure must not break route rendering.
  }
}

function normalizeCentre(value) {
  const original = stringValue(value);
  if (!original) return '';

  const normalized = original
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.includes('keru')) return 'CRHA Keru';
  if (normalized.includes('dhawa')) return 'CRHA Dhawa';
  if (normalized.includes('pratap')) return 'UHTC Pratap Nagar';
  if (
    ['dundhara', 'dhundhara', 'dundara', 'dhundara'].some((part) =>
      normalized.includes(part)
    )
  ) {
    return 'CRHA Dhundhara';
  }

  return original;
}

function stripTenantPrefix(pathname) {
  const path = stringValue(pathname) || '/';
  const withoutTenant = path.replace(/^\/t\/[^/]+(?=\/|$)/i, '');
  return withoutTenant || '/';
}

function getRouteContext(pathname) {
  const path = stripTenantPrefix(pathname);

  const citizenRoute =
    matchPath('/citizen/:citizenId/*', path) ||
    matchPath('/dashboard/citizen/:citizenId/*', path);

  const encounterRoute =
    matchPath('/citizen/:citizenId/encounter/*', path) ||
    matchPath('/dashboard/citizen/:citizenId/encounter/*', path);

  const addVisitRoute =
    matchPath('/citizen/:citizenId/add-visit', path) ||
    matchPath('/dashboard/citizen/:citizenId/add-visit', path);

  const workspaceRoute =
    matchPath('/citizen/:citizenId/visit/:visitId/workspace', path) ||
    matchPath('/dashboard/citizen/:citizenId/visit/:visitId/workspace', path);

  return {
    path,
    citizenId: decodeValue(citizenRoute?.params?.citizenId),
    visitId: decodeValue(workspaceRoute?.params?.visitId),
    onEncounter: Boolean(encounterRoute),
    onAddVisit: Boolean(addVisitRoute),
    onWorkspace: Boolean(workspaceRoute),
  };
}

function unwrapCitizen(response) {
  const payload =
    response && Object.prototype.hasOwnProperty.call(response, 'data')
      ? response.data
      : response;

  return (
    payload?.citizen ||
    payload?.data?.citizen ||
    payload?.result?.citizen ||
    payload?.data ||
    payload ||
    null
  );
}

function normalizeCitizen(value, fallbackCitizenId = '') {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const citizenId = stringValue(
    value.citizenId ||
      value.cid ||
      value.id ||
      value._id ||
      fallbackCitizenId
  );
  if (!citizenId) return null;

  // Never place an authentication secret into shared Citizen context/storage.
  const { password: _password, ...safeValue } = value;

  return {
    ...safeValue,
    citizenId,
    centre: normalizeCentre(
      safeValue.centre ||
        safeValue.healthCentre ||
        safeValue.chc ||
        ''
    ),
  };
}

function persistCitizen(citizen) {
  const normalized = normalizeCitizen(citizen);
  if (!normalized?.citizenId) return;

  writeJson('activeCitizen', normalized);

  try {
    localStorage.setItem('activeCitizenId', normalized.citizenId);
    localStorage.setItem('lastVisitedCitizenId', normalized.citizenId);

    if (normalized.centre) {
      localStorage.setItem('activeCentre', normalized.centre);
      localStorage.setItem('centre', normalized.centre);
    }
  } catch {
    // Storage failure must not invalidate a successful in-memory selection.
  }
}

function clearGlobalVisitContext() {
  try {
    // Clear only global/legacy pointers. Citizen-scoped Visit IDs and
    // Visit-scoped context are intentionally preserved so a previous Visit can
    // still be reopened safely after starting another encounter.
    localStorage.removeItem('activeVisitId');
    localStorage.removeItem('visitId');
    localStorage.removeItem('activeVisitContext');
    localStorage.removeItem('editReturnedVisit');
    localStorage.removeItem('selectedVisit');
  } catch {
    // Browser-storage cleanup is best effort.
  }
}

function setActiveVisit(citizenId, visitId) {
  const normalizedCitizenId = stringValue(citizenId);
  const normalizedVisitId = stringValue(visitId);
  if (!normalizedCitizenId || !normalizedVisitId) return;

  try {
    localStorage.setItem(
      `activeVisitId:${normalizedCitizenId}`,
      normalizedVisitId
    );
    localStorage.setItem('activeVisitId', normalizedVisitId);

    // Legacy pages still read `visitId`; keep it synchronized during migration.
    localStorage.setItem('visitId', normalizedVisitId);
  } catch {
    // In-memory routing remains usable even when storage is unavailable.
  }
}

function getStateCitizen(state) {
  if (!state || typeof state !== 'object') return null;

  return (
    state.citizen ||
    state.selectedCitizen ||
    state.activeCitizen ||
    state.data?.citizen ||
    null
  );
}

function getStateCitizenId(state, stateCitizen) {
  if (!state || typeof state !== 'object') return '';

  return stringValue(
    state.citizenId ||
      state.cid ||
      stateCitizen?.citizenId ||
      stateCitizen?.cid ||
      stateCitizen?.id ||
      stateCitizen?._id
  );
}

function getStateVisitId(state) {
  if (!state || typeof state !== 'object') return '';

  return stringValue(
    state.visitId ||
      state.activeVisitId ||
      state.visit?.visitId ||
      state.visit?._id ||
      state.visit?.id ||
      state.visitContext?.visitId
  );
}

function contextsReferToSameCitizen(context, citizenId) {
  if (!context || typeof context !== 'object') return true;

  const contextCitizenId = stringValue(
    context.citizenId ||
      context.cid ||
      context.citizen?.citizenId
  );

  return !contextCitizenId || contextCitizenId === stringValue(citizenId);
}

export default function CitizenRouteSync({ children }) {
  const location = useLocation();
  const { setCitizen } = useCitizen();
  const requestSequenceRef = useRef(0);
  const addVisitResetRef = useRef('');

  useEffect(() => {
    const routeContext = getRouteContext(location.pathname);
    const state = location.state || {};
    const stateCitizen = getStateCitizen(state);
    const stateCitizenId = getStateCitizenId(state, stateCitizen);

    // The route is authoritative. Navigation state is the next trusted source.
    const citizenId = stringValue(
      routeContext.citizenId || stateCitizenId
    );

    const query = new URLSearchParams(location.search || '');
    const explicitVisitId = stringValue(
      routeContext.visitId || query.get('visitId') || getStateVisitId(state)
    );

    const previousActiveCitizenId = stringValue(
      localStorage.getItem('activeCitizenId') ||
        readJson('activeCitizen', {})?.citizenId
    );

    // Switching Citizens must never carry another Citizen's global Visit.
    // Scoped Visit keys remain available for explicit restoration later.
    if (
      citizenId &&
      previousActiveCitizenId &&
      citizenId !== previousActiveCitizenId
    ) {
      clearGlobalVisitContext();
    }

    if (citizenId) {
      try {
        localStorage.setItem('activeCitizenId', citizenId);
        localStorage.setItem('lastVisitedCitizenId', citizenId);
      } catch {
        // Continue with in-memory state.
      }
    }

    const addVisitResetKey = routeContext.onAddVisit && citizenId
      ? `${routeContext.path}|${citizenId}`
      : '';

    if (explicitVisitId && citizenId) {
      addVisitResetRef.current = '';
      setActiveVisit(citizenId, explicitVisitId);
    } else if (routeContext.onAddVisit) {
      // Add Visit intentionally starts a new encounter. Run this reset once per
      // route/Citizen entry so an asynchronous Citizen refresh cannot erase the
      // newly-created Visit context before navigation completes.
      if (addVisitResetRef.current !== addVisitResetKey) {
        clearGlobalVisitContext();
        addVisitResetRef.current = addVisitResetKey;
      }
    } else if (routeContext.onEncounter && citizenId) {
      addVisitResetRef.current = '';
      const scopedVisitId = stringValue(
        localStorage.getItem(`activeVisitId:${citizenId}`)
      );

      if (scopedVisitId) {
        setActiveVisit(citizenId, scopedVisitId);
      } else {
        const activeContext = readJson('activeVisitContext', null);
        if (!contextsReferToSameCitizen(activeContext, citizenId)) {
          clearGlobalVisitContext();
        }
      }
    } else {
      addVisitResetRef.current = '';
    }
    // Outside encounter/Add Visit, do not clear Visit state merely because the
    // user navigated to a workspace, previous-visits page, or another module.

    if (!citizenId) {
      // A route without Citizen identity must not clear an existing selection.
      return undefined;
    }

    const cachedCitizen = normalizeCitizen(
      readJson('activeCitizen', null),
      citizenId
    );
    const matchingCachedCitizen =
      cachedCitizen?.citizenId === citizenId ? cachedCitizen : null;

    const normalizedStateCitizen = normalizeCitizen(stateCitizen, citizenId);
    const matchingStateCitizen =
      normalizedStateCitizen?.citizenId === citizenId
        ? normalizedStateCitizen
        : null;

    const immediateCitizen = normalizeCitizen(
      {
        ...(matchingCachedCitizen || {}),
        ...(matchingStateCitizen || {}),
        citizenId,
      },
      citizenId
    );

    if (immediateCitizen) {
      setCitizen(immediateCitizen);
      persistCitizen(immediateCitizen);
    } else {
      setCitizen({ citizenId });
    }

    // Always refresh through the canonical, authenticated HTTP service. This
    // keeps DOB/reported-age, centre, HMIS identity, and profile status current.
    const controller = new AbortController();
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    http
      .get(
        `/api/citizens/${encodeURIComponent(citizenId)}`,
        { signal: controller.signal }
      )
      .then((response) => {
        if (
          controller.signal.aborted ||
          requestSequence !== requestSequenceRef.current
        ) {
          return;
        }

        const loadedCitizen = normalizeCitizen(
          unwrapCitizen(response),
          citizenId
        );
        if (!loadedCitizen || loadedCitizen.citizenId !== citizenId) return;

        const mergedCitizen = normalizeCitizen(
          {
            ...(immediateCitizen || {}),
            ...loadedCitizen,
            citizenId,
          },
          citizenId
        );

        if (!mergedCitizen) return;

        setCitizen(mergedCitizen);
        persistCitizen(mergedCitizen);

        try {
          window.dispatchEvent(
            new CustomEvent('citizen:context-synced', {
              detail: {
                citizenId,
                citizen: mergedCitizen,
                visitId:
                  explicitVisitId ||
                  localStorage.getItem(`activeVisitId:${citizenId}`) ||
                  '',
              },
            })
          );
        } catch {
          // CustomEvent support is non-essential.
        }
      })
      .catch((error) => {
        if (
          controller.signal.aborted ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        // Cached/state data remains active. Log the refresh failure without
        // destroying a valid Citizen selection.
        console.warn(
          'CitizenRouteSync profile refresh failed:',
          error?.response?.data?.message ||
            error?.message ||
            'Unable to load Citizen profile'
        );
      });

    return () => {
      controller.abort();
    };
  }, [
    location.pathname,
    location.search,
    location.key,
    location.state,
    setCitizen,
  ]);

  return children;
}
